<template>
  <SafelyHydrate>
    <div class="a11y-layout-reset">
      <!-- PRE-CONTENT / BREADCRUMBS -->
      <slot name="pre-content" v-bind="slotContext" />

      <div class="pdp-grid" :class="layoutClass">
        <!-- VISUALS -->
        <div class="grid-item-visuals" data-test-id="product-visuals">
          <slot name="visuals" v-bind="slotContext" />
        </div>

        <!-- WRAPPER FOR CONTENT -->
        <div class="grid-item-content">
          <!-- TITLE -->
          <div class="grid-item-header">
            <slot name="details-header" v-bind="slotContext" />
          </div>

          <!-- PRICE -->
          <div class="grid-item-price">
            <slot name="product-price" v-bind="slotContext" />
          </div>

          <!-- PRODUCT BENEFITS -->
          <div class="grid-item-benefits">
            <slot name="product-benefits" v-bind="slotContext" />
          </div>

          <!-- COLOR PICKER -->
          <div class="grid-item-color">
            <slot name="color-picker" v-bind="slotContext" />
          </div>

          <!-- SIZE PICKER -->
          <div class="grid-item-size">
            <slot
              v-if="!isThisAPremiumClubProduct"
              name="size-picker"
              v-bind="slotContext"
              :enable-sizekick="enableSizekick"
            />
          </div>

          <!-- CTA -->
          <div class="grid-item-cta">
            <slot name="cta" v-bind="slotContext" />
          </div>

          <!-- DESKTOP MOTIVATION LAYER -->
          <div class="grid-item-motivation-layer">
            <slot
              v-if="product.id"
              name="motivation-layer"
              v-bind="slotContext"
            />
          </div>

          <!-- PRODUCT DETAILS (in-context accordion, right column, ≥640px / non-mobile) -->
          <div
            v-if="$slots['product-details']"
            class="grid-item-product-details"
          >
            <slot name="product-details" v-bind="slotContext" />
          </div>
        </div>
      </div>

      <!-- POST-CONTENT / PRODUCT DESCRIPTION -->
      <slot name="post-content" v-bind="slotContext" />

      <!-- PRODUCT SIZE GUIDE MODAL -->
      <LazyProductSizeGuideModal
        v-if="isProductSizeGuideModalOpen"
        :product="product"
      />

      <!-- MOBILE ADD TO BASKET FLOATING BUTTON -->
      <slot name="cta-fab" v-bind="slotContext" />
    </div>
  </SafelyHydrate>
</template>

<script setup lang="ts">
// =============================================================================
// IMPORTS & TYPES
// =============================================================================
import {
  ref,
  computed,
  effectScope,
  onMounted,
  onBeforeMount,
  watch,
} from 'vue'
import type { PropType } from 'vue'
import {
  getFirstAttributeValue,
  getPrice,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import {
  getSizeParamFromVariant,
  normalizeSize,
} from '#layers/base/types/variant'
import {
  isPremiumClubProduct,
  getFormattedOrderNumber,
  getProductOrderNumberFromVariant,
  getLowestPrice,
  getMatchingCupSizeVariant,
  isCupSizeVariant,
} from '~/utils/product'
import { getProductDescription } from '#layers/base/utils/advancedAttribute'
import { fetchOnNavigation } from '#layers/base/utils/loading'
import { DEFAULT_PRICE_PROMOTION_KEY } from '#layers/base/constants/withParameters'
import useZoomGalleryActions from '#layers/base/composables/ui/useZoomGalleryActions'
import useProductBreadcrumbs from '#layers/base/composables/useProductBreadcrumbs'
import { useDeviceType } from '~/composables/useDeviceType'
import usePdpSeo from '#layers/base/composables/usePdpSeo'
import useProductAlternateNames from '#layers/base/composables/useProductAlternateNames'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useUiState from '~/composables/ui/useUiState'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useWatch from '#layers/base/composables/useWatch'
import { useVariant } from '#storefront/composables'
import usePpkContext from '#layers/base/composables/usePpkContext'
import type { HyphenatedOrderNumber } from '~/types/product'
import usePdpHeadLinks from '#layers/base/composables/usePdpHeadLinks'
import { runWhenIdle } from '#layers/base/utils/idle'

// =============================================================================
// COMPONENT INTERFACE
// =============================================================================
const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  highlightedOrderNumber: {
    type: String,
    default: '',
  },
  usePromotionResolution: {
    type: Boolean,
    default: false,
  },
  enableSizekick: {
    type: Boolean,
    default: false,
  },
  enableSeo: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String as PropType<'pdp' | 'advantage'>,
    default: 'pdp',
  },
})

// =============================================================================
// ROUTE & SIZE-URL STATE
// =============================================================================
const route = useRoute()
const router = useRouter()

const sizeParamFromRoute = computed(() => {
  const size = route.query.size
  if (typeof size !== 'string') {
    return ''
  }
  return normalizeSize(size)
})

function findVariantBySizeParam(
  variants: Variant[],
  sizeParam: string,
): Variant | undefined {
  if (!sizeParam) {
    return undefined
  }
  return variants.find((v) => getSizeParamFromVariant(v) === sizeParam)
}

// =============================================================================
// APP FOUNDATION & GLOBAL STATE
// =============================================================================
const app = useNuxtApp()
const i18n = useI18n()
const globalConfig = useGlobalConfig()
const scope = effectScope()
const { init, setVariantEntries, getVariantEntry, flush, useContextCheck } =
  usePpkContext()

// =============================================================================
// CORE PRODUCT STATE & VARIANTS
// =============================================================================
const activeVariant = ref<Variant>()
const isContextSet = useContextCheck(props.product)
const safePpkInit = (orderNumber: string) => {
  if (!isContextSet.value) {
    init(props.product, orderNumber, () =>
      setVariantEntries(productVariants.value, props.product),
    )
  }
}

const pricePromotionKey = computed(() => {
  return props.highlightedOrderNumber
    ? getPricePromotionKey(props.product, props.highlightedOrderNumber)
    : undefined
})

const productOrderNumber = computed(() => {
  let orderNumber: HyphenatedOrderNumber | undefined
  const matchingCupSizeVariant = getMatchingCupSizeVariant(
    props.product,
    props.highlightedOrderNumber,
  )

  // Default order number when not set
  if (!props.highlightedOrderNumber) {
    orderNumber =
      (getProductOrderNumberFromVariant(
        activeVariant.value,
      ) as HyphenatedOrderNumber) ||
      (getFormattedOrderNumber(props.product) as
        | HyphenatedOrderNumber
        | undefined)
  }

  // PPK-based order number, PPK is always resolved based on the highlighted order number
  else if (pricePromotionKey.value) {
    orderNumber = props.highlightedOrderNumber
      .match(/.{1,4}/g)
      ?.join('-') as HyphenatedOrderNumber
  }

  // Cup size variant-based order number
  else if (matchingCupSizeVariant) {
    orderNumber = getProductOrderNumberFromVariant(
      matchingCupSizeVariant,
    ) as HyphenatedOrderNumber
  }

  // If no order number found use the full order number from the product
  // (eg. using shortened order number without last 2 digits for article placement identification)
  return (
    orderNumber ??
    (getFormattedOrderNumber(props.product) as
      | HyphenatedOrderNumber
      | undefined)
  )
})

const productVariants = computed(
  () => promotionKeyVariants.value || props.product.variants || [],
)

const isThisAPremiumClubProduct = computed(() =>
  isPremiumClubProduct(props.product),
)

watch(
  () => props.highlightedOrderNumber,
  (newOrderNumber: string) => {
    safePpkInit(newOrderNumber)
  },
)

// =============================================================================
// DEVICE CONTEXT
// =============================================================================
const { isMobile } = useDeviceType()

// =============================================================================
// PRODUCT DISPLAY DATA
// =============================================================================
const productBrand = computed(
  () => getFirstAttributeValue(props.product.attributes, 'brand')?.label,
)

const productName = computed(
  () =>
    getFirstAttributeValue(props.product.attributes, 'name')?.label ||
    i18n.t('wishlist.product'),
)

const productLongDescription = computed(() => {
  return (getProductDescription(props.product) as string)?.substring(0, 156)
})

// =============================================================================
// DATA FETCHING & API
// =============================================================================
const { refresh: fetchProductAlternateNames, data: productAlternateNames } =
  await app.runWithContext(() =>
    useProductAlternateNames({
      params: computed(() => ({ productId: props.product.id })),
      options: { immediate: false },
      key: `useProductAlternateNames-${props.product.id}`,
    }),
  )

const { data: promotionKeyVariants } = await app.runWithContext(() =>
  useVariant(
    {
      params: computed(() => ({
        ids:
          props.product.variants?.map((variant: Variant) => variant.id) || [],
        pricePromotionKey:
          pricePromotionKey.value ?? DEFAULT_PRICE_PROMOTION_KEY,
        include: {
          attributes: 'all',
        },
      })),
    },
    `variant-${props.product.id}-${productOrderNumber.value ?? ''}`,
  ),
)

const shopConfiguration = useSmartShopConfiguration()

const fetchData = async () => {
  await Promise.all([
    shopConfiguration.fetch(),
    globalConfig.fetch(),
    import.meta.server ? fetchProductAlternateNames() : undefined,
  ])
}

await fetchOnNavigation(fetchData)

// =============================================================================
// UI STATE MANAGEMENT
// =============================================================================
const {
  isProductSizeGuideModalOpen,
  isMobileSearchBarVisible,
  closeProductSizeGuide,
  setActiveProduct,
} = useUiState()

const { toggle: toggleZoomGallery } = useZoomGalleryActions()

const layoutClass = computed(() => {
  return {
    pdp: 'layout-pdp',
    advantage: 'layout-advantage',
  }[props.layout]
})

// Watch mobile state for search bar visibility
scope.run(() => {
  useWatch(
    () => isMobile,
    () => {
      if (
        props.layout === 'pdp' &&
        isMobile.value &&
        isMobileSearchBarVisible.value
      ) {
        isMobileSearchBarVisible.value = false
      }
    },
  )
})

// =============================================================================
// PROMOTION RESOLUTION
// =============================================================================
watch(
  () => productVariants.value,
  (newVariants) => {
    setVariantEntries(productVariants.value, props.product)

    if (activeVariant.value && isCupSizeVariant(activeVariant.value)) {
      activeVariant.value = findMatchingCupSize(
        newVariants,
        activeVariant.value,
      )
    } else {
      activeVariant.value = findMatchingSize(newVariants, activeVariant.value)
    }
  },
)

const handlePopState = () => flush(props.product)

// Watch browser back/forward navigation to reset the ppk context because
// the component is not unmounted when navigating back
if (import.meta.client) {
  onMounted(() => window.addEventListener('popstate', handlePopState))
  onUnmounted(() => window.removeEventListener('popstate', handlePopState))
}

// =============================================================================
// TRACKING & ANALYTICS
// =============================================================================
const { trackViewItem, fireTrackingServerQueue } = useTrackingEvents(true)

// Watch product id changes for tracking
useWatch(
  () => props.product.id,
  () => {
    activeVariant.value = undefined

    trackViewItem({
      product: props.product,
    })
  },
)

// =============================================================================
// NAVIGATION & BREADCRUMBS
// =============================================================================
const { breadcrumbs } = useProductBreadcrumbs(props.product)

// =============================================================================
// SEO MANAGEMENT
// =============================================================================
const price = computed(() => {
  return activeVariant.value
    ? getPrice(activeVariant.value)
    : getLowestPrice(productVariants.value)
})

if (props.enableSeo) {
  usePdpSeo({
    product: props.product,
    productName: productName.value,
    productLongDescription: productLongDescription.value,
    price,
    productVariants: productVariants.value,
    productAlternateNames: productAlternateNames.value,
  })
}

// =============================================================================
// EVENT HANDLERS
// =============================================================================
const handleSelectedSize = (variant: Variant) => {
  activeVariant.value = variant

  if (props.layout === 'pdp' && import.meta.client) {
    const sizeParam = getSizeParamFromVariant(variant)
    if (!sizeParam) {
      return
    }

    const query: Record<string, string> = { ...route.query, size: sizeParam }

    if (isCupSizeVariant(variant)) {
      const entry = getVariantEntry(variant, props.product)
      if (entry?.orderNumber) {
        query.ordernumber = entry.orderNumber
      }
    }

    router.replace({ path: route.path, query })
  }
}

// =============================================================================
// LIFECYCLE HOOKS
// =============================================================================
onBeforeRouteLeave(() => {
  if (isProductSizeGuideModalOpen.value) {
    closeProductSizeGuide()
  }
  toggleZoomGallery(false)
})

onBeforeMount(() => {
  if (props.layout === 'pdp' && isMobile.value) {
    isMobileSearchBarVisible.value = false
  }
})

// Preselect size from URL
function applySizeFromRoute() {
  if (props.layout !== 'pdp' || !sizeParamFromRoute.value) {
    return
  }
  const variant = findVariantBySizeParam(
    productVariants.value,
    sizeParamFromRoute.value,
  )
  if (variant) {
    activeVariant.value = variant
  }
}

watch(
  [() => productVariants.value.length, sizeParamFromRoute],
  () => applySizeFromRoute(),
  { immediate: true },
)

onMounted(() => {
  setActiveProduct(props.product)

  safePpkInit(props.highlightedOrderNumber)

  if (isThisAPremiumClubProduct.value) {
    activeVariant.value = productVariants.value[0]
  }

  applySizeFromRoute()

  trackViewItem({
    product: props.product,
    variant: activeVariant?.value ?? productVariants.value[0],
  })

  runWhenIdle(fireTrackingServerQueue)
})

// =============================================================================
// FINAL SLOT CONTEXT
// =============================================================================
const slotContext = computed(() => ({
  activeVariant: activeVariant.value,
  breadcrumbs,
  highlightedOrderNumber: props.highlightedOrderNumber,
  pricePromotionKey: pricePromotionKey.value,
  product: props.product!,
  productBrand: productBrand.value!,
  productLongDescription: productLongDescription.value!,
  productName: productName.value!,
  productOrderNumber: productOrderNumber.value!,
  productVariants: productVariants.value,
  shopConfiguration: shopConfiguration.data.value!,
  isActiveSalePeriod: shopConfiguration.isActiveSalePeriod.value,
  showMoreDetailsButton: props.layout === 'advantage',
  showShippingCostAndTaxHints: props.layout === 'pdp',
  handleSelectedSize: handleSelectedSize,
}))

usePdpHeadLinks(() => props.product, productAlternateNames)
</script>

<style lang="scss" scoped>
.pdp-grid {
  display: grid;
  column-gap: 2rem;
  @apply gap-y-2;

  &.layout-pdp {
    @apply grid-cols-2 lg:grid-cols-[40%_1fr];
    @apply grid-rows-[repeat(6,auto)];

    .grid-item-visuals {
      @apply col-[1/span_2]  lg:col-[1/span_1] lg:row-[1/span_7];
      // Sticky product image during vertical scroll (ECOM-2770, LEAP winner).
      // Applied at lg where the two-column layout exists so the image has a
      // scroll partner; self-start keeps the item at content height so the
      // sticky travel works. top-[100px] mirrors the LEAP offset.
      @apply lg:sticky lg:top-[100px] lg:self-start;
    }

    .grid-item-content {
      @apply col-[1/span_2] lg:col-[2/span_2] w-full pb-0 sm:pb-16 md:px-8 md:pb-0 lg:px-0 lg:pr-8;
      @apply grid grid-cols-2 gap-x-1 gap-y-2;
    }

    .grid-item-header {
      @apply col-[1/span_2] row-[1/span_1] lg:row-[1/span_1];
    }

    .grid-item-price {
      @apply col-[1/span_1] row-[2/span_1] lg:row-[2/span_1] lg:col-[1/span_2];
      @apply md:mt-6;
    }

    .grid-item-benefits {
      @apply col-[2/span_1] row-[2/span_1] md:row-[3/span_1] md:col-[1/span_2] lg:row-[3/span_1];
      @apply sm:mt-3;
    }

    .grid-item-color {
      @apply col-[1/span_3] row-[3/span_1] sm:col-[2/span_1] md:col-[1/span_1] md:row-[4/span_1] lg:col-[1/span_2] lg:row-[4/span_1];
    }

    .grid-item-size {
      @apply col-[1/span_2] row-[4/span_1] md:row-[5/span_1] lg:col-[1/span_2] lg:row-[5/span_1];
      @apply mt-2;
    }

    .grid-item-cta {
      @apply col-[1/span_2] row-[5/span_1] md:row-[6/span_1] lg:row-[6/span_1];
    }

    .grid-item-motivation-layer {
      @apply col-[1/span_2] lg:px-0;
    }

    // In-context product-details accordion (ECOM-2770). Lives inside
    // .grid-item-content (the buy-box column), so it sits under the CTA in the
    // right column at >=lg while the product image stays sticky on the left —
    // not full-width under the image. col-[1/span_2] spans the content's inner
    // two columns; below lg the content column is full-width so it stacks
    // naturally under the buy box.
    .grid-item-product-details {
      @apply col-[1/span_2];
      @apply mt-6; // spacing below the buy box
    }
  }

  &.layout-advantage {
    @apply grid-cols-[112px_repeat(2,1fr)] smaller:grid-cols-[160px_repeat(2,1fr)] sm:grid-cols-[230px_repeat(2,1fr)] md:grid-cols-[275px_repeat(2,1fr)];
    @apply grid-rows-[auto_min-content_auto_auto_auto_auto];

    .grid-item-visuals {
      @apply col-[1/span_1] row-[1/span_4] self-start;
    }

    .grid-item-content {
      @apply col-span-2;
      @apply grid gap-y-2;

      display: contents;
    }

    .grid-item-price {
      @apply col-[2/span_1] row-[1/span_1];
    }

    .grid-item-benefits {
      @apply col-[1/span_3] smaller:col-[3/span_1] smaller:row-[1/span_1];
    }

    .grid-item-color {
      @apply col-[1/span_3] sm:col-[2/span_2] md:row-[2/span_1];
      @apply mt-3;
    }

    .grid-item-size {
      @apply col-[1/span_3] md:col-[2/span_2] md:row-[3/span_1] mt-2;
    }

    .grid-item-cta {
      @apply col-[1/span_3] row-[auto/span_1] lg:col-[2/span_2] lg:row-[4/span_1];
      @apply mt-2;
    }
  }
}
</style>
